import { useState } from 'react';
import { entra } from '../../lib/sessione';
import { ErroreApi, ErroreRete } from '../../api/client';
import { inviti } from '../../api/inviti';

/*  La schermata d'accesso.

    Tre cose fatte apposta, e due non fatte apposta.

    IL MESSAGGIO D'ERRORE È QUELLO DEL SERVER, parola per parola. Il server risponde la
    stessa frase a «questo codice non esiste» e a «la password è sbagliata» — di proposito,
    perché distinguerli regala a un estraneo l'elenco dei codici validi — e riscrivere qui
    un messaggio più gentile vorrebbe dire reintrodurre la distinzione dal lato sbagliato.

    IL CODICE SI RICORDA, LA PASSWORD NO. Il nome con cui si entra è lo stesso ogni
    mattina e riscriverlo è attrito inutile; la password no, e un campo che si ricompila da
    solo su un computer d'ufficio è una password che sa anche chi si siede dopo.

    «PASSWORD DIMENTICATA» C'È SOLO DOVE C'È LA POSTA. Fino al 10/10/2026 non c'era
    affatto, e il motivo scritto qui era: non esiste nessuna posta da cui far partire un
    collegamento, e un bottone che manda una mail che non parte è peggio di nessun bottone
    — chi lo preme aspetta. Il motivo è ancora buono, quindi il bottone compare solo
    quando il server dice che la posta è configurata (`postaCollegata`), e sulle
    installazioni senza tenant la schermata resta quella di prima, con la riga che dice di
    rivolgersi a chi gestisce le utenze.

    NIENTE «RICORDAMI». La sessione dura otto ore e muore con il browser: su un computer
    condiviso in ufficio è la differenza fra «ha finito il turno» e «chi si siede dopo è
    lui».
*/
export function Accesso({
  onEntrato,
  postaCollegata,
}: {
  onEntrato: () => void;
  postaCollegata: boolean;
}) {
  const [codice, setCodice] = useState(() => ultimoCodice() ?? '');
  const [dimenticata, setDimenticata] = useState(false);
  const [password, setPassword] = useState('');
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  async function prova(evento: React.FormEvent) {
    evento.preventDefault();
    setErrore(null);
    setInCorso(true);

    try {
      await entra(codice.trim(), password);
      ricordaCodice(codice.trim());
      /*  La password si cancella prima di ricaricare: resta in memoria finché questo
          componente vive, e da qui in poi non serve più a niente.  */
      setPassword('');
      onEntrato();
    } catch (guasto) {
      setErrore(
        guasto instanceof ErroreRete
          ? 'TeraWorks non risponde: non è un problema di password.'
          : guasto instanceof ErroreApi
            ? guasto.message
            : 'Accesso non riuscito.',
      );
      setInCorso(false);
    }
  }

  return (
    <div className="accesso">
      <form onSubmit={(e) => void prova(e)}>
        <h1>TeraWorks</h1>

        <label className="campo-blocco">
          <span>Codice</span>
          <input
            value={codice}
            onChange={(e) => setCodice(e.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            // Il cursore parte qui solo se il codice non c'è già: chi torna domani
            // mattina deve trovarlo nella password, non doverci arrivare col tabulatore.
            autoFocus={codice.length === 0}
            required
          />
        </label>

        <label className="campo-blocco">
          <span>Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            autoFocus={codice.length > 0}
            required
          />
        </label>

        {errore !== null && <p className="errore">{errore}</p>}

        <button type="submit" className="primario" disabled={inCorso}>
          {inCorso ? 'Verifica…' : 'Entra'}
        </button>

        {postaCollegata ? (
          <p className="nota">
            <button type="button" className="come-link" onClick={() => setDimenticata(true)}>
              Password dimenticata
            </button>
          </p>
        ) : (
          <p className="nota">
            Se la password non si ricorda, la reimposta chi in azienda gestisce le utenze.
          </p>
        )}
      </form>

      {dimenticata && <Dimenticata onChiudi={() => setDimenticata(false)} />}
    </div>
  );
}

/*  LA RICHIESTA DEL LINK, E PERCHÉ LA RISPOSTA È SEMPRE LA STESSA.

    Il server risponde la stessa frase per un indirizzo che conosce e per uno che non
    conosce, e questa schermata la mostra parola per parola senza aggiungerci niente.
    Distinguere i due casi qui — «non risulta nessuno con questo indirizzo» — rimetterebbe
    dal lato sbagliato la differenza che il server toglie apposta: chiunque potrebbe
    chiedere a TeraWorks chi lavora in azienda, un indirizzo alla volta, senza entrare.

    È la stessa regola per cui codice sbagliato e password sbagliata, qui sopra, danno lo
    stesso messaggio.  */
function Dimenticata({ onChiudi }: { onChiudi: () => void }) {
  const [indirizzo, setIndirizzo] = useState('');
  const [esito, setEsito] = useState<string | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  async function chiedi(evento: React.FormEvent) {
    evento.preventDefault();
    setErrore(null);
    setInCorso(true);

    try {
      const risposta = await inviti.passwordDimenticata(indirizzo.trim());
      setEsito(risposta.messaggio);
    } catch (guasto) {
      setErrore(
        guasto instanceof ErroreRete
          ? 'TeraWorks non risponde: la richiesta non è partita.'
          : guasto instanceof ErroreApi
            ? guasto.message
            : 'Richiesta non riuscita.',
      );
    } finally {
      setInCorso(false);
    }
  }

  if (esito !== null) {
    return (
      <div className="riquadro">
        <p>{esito}</p>
        <p className="nota">Il link vale due ore e una volta sola.</p>
        <button type="button" onClick={onChiudi}>
          Chiudi
        </button>
      </div>
    );
  }

  return (
    <form className="riquadro" onSubmit={(e) => void chiedi(e)}>
      <label className="campo-blocco">
        <span>Il tuo indirizzo di posta</span>
        <input
          type="email"
          value={indirizzo}
          onChange={(e) => setIndirizzo(e.target.value)}
          autoComplete="email"
          autoFocus
          required
        />
      </label>

      <p className="nota">
        Quello scritto nella tua anagrafica di TeraWorks. Se non lo sai, lo sa chi gestisce
        le utenze.
      </p>

      {errore !== null && <p className="errore">{errore}</p>}

      <button type="submit" className="primario" disabled={inCorso}>
        {inCorso ? 'Invio…' : 'Mandami il link'}
      </button>
      <button type="button" onClick={onChiudi}>
        Annulla
      </button>
    </form>
  );
}

/*  Il codice dell'ultimo accesso, nel browser di chi lo ha usato. Non è una credenziale e
    non apre niente: è il nome che c'era scritto nella casella ieri.  */
const CHIAVE = 'teraworks.ultimo-codice';

function ultimoCodice(): string | null {
  try {
    return window.localStorage.getItem(CHIAVE);
  } catch {
    return null;
  }
}

function ricordaCodice(codice: string): void {
  try {
    window.localStorage.setItem(CHIAVE, codice);
  } catch {
    /* si riscriverà a mano domani */
  }
}
